import { getFormattedCurrency } from '@svs/nova-poolbetting/core/lib/client';
import { gsap } from 'gsap';

type CounterEffectOptions = {
  duration?: number;
  end: number;
  ease?: string;
  increment?: number;
};

/**
 * A custom effect that can be used with gsap registerEffect method.
 * The effect takes a target html element and increments the value of the innerText property of that element.
 * It uses modifiers-plugin to alter the format of the value to present number with space as thousands separator.
 *
 * @param targets HTML element selector string
 * @param options Configuration options for the counter effect
 * @param options.duration Animation duration in secs
 * @param options.end The end value for the counter
 * @param options.ease The choice of gsap ease
 * @param options.increment The interval number to snap increments to
 * @throws {Error} Error if the target element is not found
 * @returns gsap tween containing the counter animation
 */
export function gsapCounterEffect(targets: HTMLElement[], options: Partial<CounterEffectOptions> = {}): object {
  const defaults = {
    end: 0,
    duration: 3,
    ease: 'power2.out',
    increment: 1,
  };

  const mergedOptions = { ...defaults, ...options };
  const [counterEl] = targets;

  if (!counterEl) {
    throw new Error('Counter effect requires a target element');
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- textContent is nullable per DOM spec
  counterEl.textContent = getFormattedCurrency(counterEl.textContent ?? '0');

  return gsap.to(counterEl, {
    duration: mergedOptions.duration,
    modifiers: {
      innerText(innerText) {
        const numericValue = Number(innerText);
        const currencyValue = Number.isFinite(numericValue) ? numericValue : 0;
        const snappedValue = gsap.utils.snap(mergedOptions.increment, currencyValue);
        return getFormattedCurrency(snappedValue);
      },
    },
    innerText: mergedOptions.end,
    ease: mergedOptions.ease,
  });
}
